'use server';
import { actionErrorLog } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import type { WizardScenario } from '@/contexts/content/types/enums';
import { logger } from '@/shared/infrastructure/logging';

const log = logger.child({ context: 'startContentCreation' });

export interface StartContentCreationParams {
  scenario: WizardScenario;
  sourceEntityId?: string;
  params?: {
    prompt?: string;
    text?: string;
    title?: string;
    fromTextMode?: 'verbatim' | 'translate' | 'recast_hero' | 'based_on' | 'incorporate';
    fromTextHeroName?: string;
    fromTextReplacements?: Array<{ from: string; to: string }>;
    fromTextChangeVector?: string;
    fromTextBasedOnMode?: 'retell' | 'reimagine';
    fromTextInjectHeroes?: string[];
    language?: string;
    storyType?: string;
    ageGroup?: string;
    theme?: string;
    setting?: string;
    energy?: number;
    characterIds?: string[];
    characterAssignments?: Array<{ characterId: string; role: string }>;
    desiredDurationMinutes?: number;
    voiceSettings?: Record<string, unknown>;
    illustrationStyle?: string;
    illustrationFormat?: string;
    autoGenerateAudioBlocks?: boolean;
    autoAssembleAudio?: boolean;
    autoGenerateCharacterPortraits?: boolean;
    autoGeneratePageImages?: boolean;
    /** Exports the order makes by itself (v2 export layers: PDF / print-ready / video). */
    exports?: {
      /** true, or the file's options (pipeline-v2 pdfExportOptions) */
      pdf?: boolean | { paper?: 'a4' | 'trim'; edgeFill?: boolean; hd?: boolean; archive?: boolean; imagesOnly?: boolean };
      print?: boolean;
      video?: boolean;
    };
    /** User consented to publish this story publicly (gallery/YouTube + text reuse) → 5% rebate. */
    publicConsent?: boolean;
    textTosAccepted?: boolean;
    /** From-text: public story whose text seeds this one (based_on reuse). */
    basisStoryId?: string;
    /**
     * "Hero from a photo" drafts: the uploaded photo and the name it goes by.
     * The character is drawn on the worker while the story is written, so this
     * travels with the plan rather than creating anything up front.
     */
    pendingPhotoHeroes?: Array<{ name: string; photoKey: string }>;
    /** Heroes made from a photo (records already in the library, ⊂ characterIds): drawn from their 3D identity. */
    photoHeroIds?: string[];
  };
}

export interface StartContentCreationResult {
  /** = orderId (v2 orders have no v1 content plan; kept for the callers that pass it on) */
  planId: string;
  storyId: string;
  jobId?: string;
  /** the pipeline v2 order (its layers: getOrderLayers(storyId)) */
  orderId?: string;
}

export async function startContentCreation(
  params: StartContentCreationParams,
): Promise<ActionResult<StartContentCreationResult>> {
  const familyId = await SessionService.getFamilyId();
  const userId = await SessionService.getUserId();
  if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

  try {
    /**
     * Pipeline v2 is the only executor: the order's estimate (upper bound,
     * only what starts by itself) is checked against the allowance BEFORE
     * anything is written — a short allowance refuses the order whole
     * («remove something or top up»); then the order plan writes every layer,
     * each layer holds its part, and the story run starts.
     */
    const { startOrderV2 } = await import('@/contexts/pipeline-v2/executor/server');
    const result = await startOrderV2({
      familyId,
      userId,
      scenario: params.scenario,
      sourceEntityId: params.sourceEntityId,
      params: params.params as Record<string, unknown> | undefined,
    });
    if (!result.ok) {
      if (result.code === 'INSUFFICIENT_ALLOWANCE') {
        log.warn('order refused: allowance short', { familyId, scenario: params.scenario, needed: result.needed, available: result.available });
        return {
          success: false,
          error: 'insufficient_allowance',
          code: 'INSUFFICIENT_ALLOWANCE',
          allowance: { needed: result.needed, available: result.available, pages: 0 },
        };
      }
      return { success: false, error: result.error };
    }

    return {
      success: true,
      data: {
        // v2 orders have no v1 content plan; the order id stands in for it
        planId: result.orderId,
        storyId: result.storyId,
        orderId: result.orderId,
      },
    };
  } catch (e: unknown) {
    // Free text-gen fair-use throttle → soft-push to subscribe, NOT a hard error.
    // The orchestrator (via the executor) throws TextGenRateLimitError with code
    // 'TEXT_RATE_LIMIT'; the wizard renders this variant as a friendly upgrade
    // nudge rather than the generic error banner.
    if (isTextRateLimit(e)) {
      return {
        success: false,
        error: e.message,
        code: 'TEXT_RATE_LIMIT',
        retryAfterSec: e.retryAfterSec,
      };
    }
    // Out of monthly allowance (premium metered incl. text) → upsell, not a raw error.
    if (isInsufficientAllowance(e)) {
      return { success: false, error: 'insufficient_allowance', code: 'INSUFFICIENT_ALLOWANCE' };
    }
    actionErrorLog('createContentPlan', e);
    return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
  }
}

/** Duck-typed guard for the free text-gen rate-limit (decoupled from the server-only class). */
function isTextRateLimit(
  e: unknown,
): e is { code: 'TEXT_RATE_LIMIT'; retryAfterSec: number; message: string } {
  return (
    typeof e === 'object' &&
    e !== null &&
    (e as { code?: unknown }).code === 'TEXT_RATE_LIMIT'
  );
}

/** Duck-typed guard: the billing adapter throws "Insufficient tokens (billing: insufficient_allowance)". */
function isInsufficientAllowance(e: unknown): boolean {
  const m = e instanceof Error ? e.message : String(e);
  return /insufficient/i.test(m) && /(tokens|allowance)/i.test(m);
}
